import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  IconButton,
  Paper,
  Chip,
  Tooltip,
  MenuItem
} from '@mui/material';
import { CloudDownload, DeleteOutline, Upload } from '@mui/icons-material';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';

const ENTITY_CONFIG = {
  inventory: {
    label: 'Inventory Item Documents',
    basePath: '/api/store-support/inventory'
  },
  tool: {
    label: 'Tool Documents',
    basePath: '/api/store-support/tools'
  },
  receiving: {
    label: 'Receiving Documents',
    basePath: '/api/store-support/receiving'
  },
  purchaseorder: {
    label: 'Purchase Order Documents',
    basePath: '/api/store/purchase-orders'
  }
};

const PURCHASE_ORDER_CATEGORY_OPTIONS = [
  { value: 'PO_ATTACHMENT', label: 'PO Attachment' },
  { value: 'PO_RECEIPT', label: 'PO Receipt' }
];

const getCategoryLabel = (doc, isPurchaseOrder) => {
  const category = doc?.category || 'GENERAL';
  if (!isPurchaseOrder) return category;
  const map = {
    PO_ATTACHMENT: 'PO Attachment',
    PO_RECEIPT: 'PO Receipt',
    RECEIPT: 'PO Receipt'
  };
  return map[category] || category;
};

const StoreDocumentsDialog = ({
  open,
  onClose,
  entityId,
  entityType = 'inventory',
  entityLabel,
  readOnly = false
}) => {
  const { showToast } = useToast();
  const [documents, setDocuments] = useState([]);
  const [category, setCategory] = useState('');
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloadingDocId, setDownloadingDocId] = useState(null);
  const normalizedType = entityType?.toLowerCase?.();
  const config = ENTITY_CONFIG[normalizedType] || ENTITY_CONFIG.inventory;
  const isPurchaseOrder = normalizedType === 'purchaseorder';
  const [isDragging, setIsDragging] = useState(false);
  const [deletingDocId, setDeletingDocId] = useState(null);

  useEffect(() => {
    if (open && entityId) {
      fetchDocuments();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, entityId, entityType]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${config.basePath}/${entityId}/documents`);
      setDocuments(response.data || []);
    } catch (error) {
      console.error('Error loading documents:', error);
      showToast('Failed to load documents', 'error');
    } finally {
      setLoading(false);
    }
  };

  const uploadFiles = async (filesList) => {
    if (!filesList || filesList.length === 0) return;
    try {
      setUploading(true);
      const formData = new FormData();
      Array.from(filesList).forEach((file) => formData.append('documents', file));
      const uploadCategory = isPurchaseOrder ? (category || 'PO_ATTACHMENT') : category;
      if (uploadCategory) formData.append('category', uploadCategory);
      await axios.post(`${config.basePath}/${entityId}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      showToast('Documents uploaded', 'success');
      setCategory('');
      fetchDocuments();
    } catch (error) {
      console.error('Upload failed:', error);
      showToast(error.response?.data?.message || 'Failed to upload documents', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleUpload = async (event) => {
    const files = event.target.files;
    await uploadFiles(files);
    event.target.value = '';
  };

  const handleDownload = async (doc) => {
    if (!doc?._id || downloadingDocId) return;
    setDownloadingDocId(doc._id);
    try {
      const response = await axios.get(
        `${config.basePath}/${entityId}/documents/${doc._id}/download`,
        { responseType: 'blob' }
      );

      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = doc.fileName || doc.originalName || 'document';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed:', error);
      showToast(error.response?.data?.message || 'Failed to download document', 'error');
    } finally {
      setDownloadingDocId(null);
    }
  };

  const handleDelete = async (docId) => {
    if (!docId || deletingDocId) return;
    setDeletingDocId(docId);
    try {
      await axios.delete(`${config.basePath}/${entityId}/documents/${docId}`);
      showToast('Document deleted', 'success');
      setDocuments((prev) => prev.filter((d) => d._id !== docId));
    } catch (error) {
      console.error('Delete failed:', error);
      showToast(error.response?.data?.message || 'Failed to delete document', 'error');
    } finally {
      setDeletingDocId(null);
    }
  };

  const handleClose = () => {
    setDocuments([]);
    setCategory('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {readOnly ? config.label.replace('Documents', 'Documents (view only)') : config.label}
        {entityLabel && (
          <Typography variant="subtitle2" color="text.secondary">
            {entityLabel}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {!readOnly && (
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Upload Documents
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {isPurchaseOrder ? (
                <TextField
                  select
                  label="Document Type"
                  size="small"
                  value={category || 'PO_ATTACHMENT'}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  {PURCHASE_ORDER_CATEGORY_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <TextField
                  label="Category / Notes"
                  size="small"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  placeholder="e.g., Cert, Photo, Inspection Sheet"
                />
              )}
              <Box
                sx={{
                  border: '2px dashed',
                  borderColor: isDragging ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  p: 3,
                  textAlign: 'center',
                  backgroundColor: isDragging ? 'action.hover' : 'transparent',
                  transition: 'all 0.2s ease'
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  setIsDragging(false);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDragging(false);
                  if (uploading) return;
                  const files = e.dataTransfer.files;
                  uploadFiles(files);
                }}
              >
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Drag & drop files here, or click Browse to select
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<Upload />}
                  disabled={uploading || deletingDocId != null}
                >
                  {uploading ? 'Uploading...' : 'Browse'}
                  <input type="file" hidden multiple onChange={handleUpload} />
                </Button>
              </Box>
            </Box>
          </Paper>
          )}

          <Paper sx={{ p: 0 }}>
            {loading ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Loading documents...
                </Typography>
              </Box>
            ) : documents.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No documents have been uploaded yet.
                </Typography>
              </Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Uploaded</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {documents.map((doc) => (
                    <TableRow key={doc._id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body2">
                            {doc.fileName || doc.originalName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {doc.fileType || doc.mimeType || 'n/a'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={getCategoryLabel(doc, isPurchaseOrder)} size="small" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {doc.mimeType || 'n/a'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {doc.uploadedAt
                            ? new Date(doc.uploadedAt).toLocaleString()
                            : 'n/a'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Download">
                          <IconButton
                            size="small"
                            onClick={() => handleDownload(doc)}
                            disabled={downloadingDocId === doc._id}
                          >
                            <CloudDownload fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {!readOnly && (
                        <Tooltip title="Delete">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(doc._id)}
                              disabled={deletingDocId === doc._id}
                            >
                              <DeleteOutline fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default StoreDocumentsDialog;

